import React, { useEffect, useRef, useState } from 'react'
import {
  FiActivity,
  FiArrowRight,
  FiBriefcase,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiCompass,
  FiCpu,
  FiEye,
  FiHeart,
  FiLayers,
  FiMessageCircle,
  FiRefreshCw,
  FiRotateCw,
  FiShield,
  FiSliders,
  FiTarget,
  FiVolume2,
  FiZap,
} from 'react-icons/fi'
import FinalCtaPanel from '../FinalCtaPanel'
import { APP_ENTRY_HREF } from '../../constants/navigation'
import './useCase.css'
import useCaseHero from '../../assets/usecase/usecase-hero.webp'
import professionalImage from '../../assets/usecase/profile-professional.webp'
import professionalThumbnail from '../../assets/usecase/profile-professional-thumb.webp'
import heritageImage from '../../assets/usecase/profile-heritage.webp'
import heritageThumbnail from '../../assets/usecase/profile-heritage-thumb.webp'
import dormantImage from '../../assets/usecase/profile-dormant.webp'
import dormantThumbnail from '../../assets/usecase/profile-dormant-thumb.webp'
import travelerImage from '../../assets/usecase/profile-traveler.webp'
import travelerThumbnail from '../../assets/usecase/profile-traveler-thumb.webp'

const phaseMeta = {
  observe: {
    number: '01',
    Icon: FiEye,
    labelFr: 'Capte l’intention',
    labelEn: 'Reads the intent',
    noteFr: 'Pourquoi maintenant ?',
    noteEn: 'Why now?',
  },
  decide: {
    number: '02',
    Icon: FiCpu,
    labelFr: 'Décide du prochain effort',
    labelEn: 'Chooses the next effort',
    noteFr: 'Le meilleur pas suivant',
    noteEn: 'The best next step',
  },
  adapt: {
    number: '03',
    Icon: FiSliders,
    labelFr: 'Ajuste en direct',
    labelEn: 'Adapts live',
    noteFr: 'Rythme, aide, complexité',
    noteEn: 'Pace, support, complexity',
  },
  verify: {
    number: '04',
    Icon: FiCheckCircle,
    labelFr: 'Vérifie le transfert',
    labelEn: 'Checks real-world transfer',
    noteFr: 'Disponible hors de la scène',
    noteEn: 'Available beyond the scene',
  },
}

const profiles = [
  {
    id: 'professional',
    number: '01',
    Icon: FiBriefcase,
    accent: '104, 211, 255',
    image: professionalImage,
    thumbnail: professionalThumbnail,
    imagePosition: 'center 45%',
    titleFr: 'Professionnels cosmopolites',
    titleEn: 'Global professionals',
    priorityFr: 'Impact immédiat',
    priorityEn: 'Immediate impact',
    situationFr: 'Réunion client demain. Le français est solide, mais le registre et la spontanéité s’effacent sous pression.',
    situationEn: 'Client meeting tomorrow. Their French is strong, but register and spontaneity fade under pressure.',
    metrics: [
      ['Échéance', 'Horizon', 'Demain', 'Tomorrow'],
      ['Fenêtre', 'Window', '12 min', '12 min'],
      ['Point sensible', 'Sensitive point', 'Registre', 'Register'],
    ],
    calibrations: [
      ['Cadence', 'Pace', 'Vive', 'Brisk', 88],
      ['Niveau d’aide', 'Support level', 'Discret', 'Discreet', 26],
      ['Pression réaliste', 'Real-world pressure', 'Élevée', 'High', 84],
      ['Preuve de progrès', 'Evidence of progress', 'Transfert', 'Transfer', 92],
    ],
    fingerprintFr: '12 min · rythme vif · aide discrète · priorité : impact',
    fingerprintEn: '12 min · brisk pace · discreet support · priority: impact',
    rationaleFr: 'L’enjeu est proche et la langue est déjà solide : OctoSynapse investit dans la spontanéité et le registre, pas dans une révision générale.',
    rationaleEn: 'The moment is close and the language is already strong: OctoSynapse invests in spontaneity and register, not a general review.',
    phases: [
      {
        id: 'observe',
        titleFr: 'Cadrer l’enjeu',
        titleEn: 'Frame the moment',
        textFr: 'Temps disponible, objectif, latence de réponse et glissement de registre deviennent les signaux de départ.',
        textEn: 'Available time, goal, response latency and register drift become the starting signals.',
      },
      {
        id: 'decide',
        titleFr: 'Parler sous pression',
        titleEn: 'Speak under pressure',
        textFr: 'Prioriser les prises de parole à fort enjeu ; augmenter le défi dès que la fluidité se stabilise.',
        textEn: 'Prioritize high-stakes speaking turns; raise the challenge once flow stabilizes.',
      },
      {
        id: 'adapt',
        titleFr: 'Affiner le registre',
        titleEn: 'Refine the register',
        textFr: 'Scène courte à embranchements, indices minimaux, objections imprévues et reformulations professionnelles après la réponse.',
        textEn: 'Short branching scenario, minimal cues, unexpected objections and professional rephrases after the response.',
      },
      {
        id: 'verify',
        titleFr: 'Réutiliser au travail',
        titleEn: 'Carry it into work',
        textFr: 'Défendre une idée sans indice et changer de registre en une seule prise.',
        textEn: 'Defend an idea without a cue and shift register in one take.',
      },
    ],
    responseTitleFr: 'Ne pas interrompre',
    responseTitleEn: 'Don’t interrupt',
    responseFr: 'Conserver la pression réaliste, attendre la fin de l’idée, puis proposer une reformulation de registre au débrief.',
    responseEn: 'Keep realistic pressure, let the idea land, then offer a register-focused rephrase in the recap.',
    microRuleFr: 'Correction après la prise',
    microRuleEn: 'After-turn feedback',
  },
  {
    id: 'heritage',
    number: '02',
    Icon: FiHeart,
    accent: '255, 184, 94',
    image: heritageImage,
    thumbnail: heritageThumbnail,
    imagePosition: 'center 46%',
    titleFr: 'Apprenants patrimoniaux',
    titleEn: 'Heritage learners',
    priorityFr: 'Voix personnelle',
    priorityEn: 'Personal voice',
    situationFr: 'La langue parlée à la maison est comprise, mais la réponse vient souvent dans une autre langue. Réunion familiale dans six semaines.',
    situationEn: 'They understand the language spoken at home but often reply in another language. Family gathering in six weeks.',
    metrics: [
      ['Échéance', 'Horizon', '6 semaines', '6 weeks'],
      ['Session', 'Session', '20 min', '20 min'],
      ['Pont à créer', 'Bridge to build', 'Comprendre → parler', 'Understand → speak'],
    ],
    calibrations: [
      ['Cadence', 'Pace', 'Réflexive', 'Reflective', 46],
      ['Niveau d’aide', 'Support level', 'Familier', 'Familiar', 72],
      ['Pression réaliste', 'Real-world pressure', 'Modulée', 'Regulated', 34],
      ['Preuve de progrès', 'Evidence of progress', 'Récit personnel', 'Personal story', 88],
    ],
    fingerprintFr: '20 min · rythme réflexif · aide familière · priorité : voix personnelle',
    fingerprintEn: '20 min · reflective pace · familiar support · priority: personal voice',
    rationaleFr: 'La compréhension existe déjà : le parcours protège la variété choisie et construit un pont vers une parole personnelle.',
    rationaleEn: 'Comprehension is already there: the journey protects the chosen variety and builds a bridge into personal speech.',
    phases: [
      {
        id: 'observe',
        titleFr: 'Choisir sa variété',
        titleEn: 'Choose your variety',
        textFr: 'Écart compréhension-expression, variété choisie, expressions familières et charge émotionnelle guident le départ.',
        textEn: 'Comprehension-to-speaking gap, chosen variety, familiar expressions and emotional load guide the start.',
      },
      {
        id: 'decide',
        titleFr: 'Retrouver les mots du foyer',
        titleEn: 'Recover home words',
        textFr: 'Privilégier le sens personnel et les variantes légitimes ; moduler l’intensité émotionnelle.',
        textEn: 'Prioritize personal meaning and legitimate variants; regulate emotional intensity.',
      },
      {
        id: 'adapt',
        titleFr: 'Raconter avec sa voix',
        titleEn: 'Tell it in your voice',
        textFr: 'Contextes familiers, feedback respectueux de la variété, amorces narratives et retour temporaire à l’écoute si nécessaire.',
        textEn: 'Familiar contexts, variety-aware feedback, narrative prompts and a temporary return to listening when needed.',
      },
      {
        id: 'verify',
        titleFr: 'Transmettre sans s’effacer',
        titleEn: 'Pass it on without self-erasing',
        textFr: 'Raconter un souvenir personnel, réutiliser des expressions familiales et garder le fil avec aisance.',
        textEn: 'Tell a personal memory, reuse family expressions and hold the thread with ease.',
      },
    ],
    responseTitleFr: 'Respecter la voix',
    responseTitleEn: 'Respect the voice',
    responseFr: 'Proposer un fragment familier dans la variété choisie, ou revenir à l’écoute sans pénalité.',
    responseEn: 'Offer a familiar fragment in the chosen variety, or return to listening without penalty.',
    microRuleFr: 'Variété avant correction',
    microRuleEn: 'Variety before correction',
  },
  {
    id: 'dormant',
    number: '03',
    Icon: FiRefreshCw,
    accent: '163, 145, 255',
    image: dormantImage,
    thumbnail: dormantThumbnail,
    imagePosition: 'center 44%',
    titleFr: 'Apprenants dormants',
    titleEn: 'Dormant learners',
    priorityFr: 'Confiance retrouvée',
    priorityEn: 'Restored confidence',
    situationFr: 'Beaucoup de mots sont reconnus, mais tout semble oublié. Le premier besoin n’est pas un niveau : c’est une preuve que la langue est encore là.',
    situationEn: 'They recognize a lot but feel they have forgotten everything. Their first need is not a level—it is proof the language is still there.',
    metrics: [
      ['Interruption', 'Pause', 'Longue', 'Long'],
      ['Session', 'Session', '18 min', '18 min'],
      ['Écart observé', 'Observed gap', 'Reconnaître > rappeler', 'Recognize > recall'],
    ],
    calibrations: [
      ['Cadence', 'Pace', 'Douce', 'Gentle', 34],
      ['Niveau d’aide', 'Support level', 'Dégressif', 'Fading', 86],
      ['Pression réaliste', 'Real-world pressure', 'Sans chrono', 'No countdown', 18],
      ['Preuve de progrès', 'Evidence of progress', 'Rappel libre', 'Free recall', 74],
    ],
    fingerprintFr: '18 min · rythme doux · aide dégressive · priorité : confiance',
    fingerprintEn: '18 min · gentle pace · fading support · priority: confidence',
    rationaleFr: 'Le premier obstacle est la confiance : le parcours valorise la récupération d’un mot avant la vitesse ou le niveau.',
    rationaleEn: 'The first obstacle is confidence: the journey values retrieving a word before speed or level.',
    phases: [
      {
        id: 'observe',
        titleFr: 'Reconnaître',
        titleEn: 'Recognize',
        textFr: 'Écart reconnaissance-rappel libre, confiance déclarée, aides utilisées et réponses sans indice dessinent le point de départ.',
        textEn: 'Recognition-to-free-recall gap, self-reported confidence, support used and unaided answers shape the starting point.',
      },
      {
        id: 'decide',
        titleFr: 'Retrouver',
        titleEn: 'Retrieve',
        textFr: 'Commencer sous le seuil de pression ; retirer l’aide seulement après un rappel réussi.',
        textEn: 'Begin below the pressure threshold; fade support only after successful recall.',
      },
      {
        id: 'adapt',
        titleFr: 'Répondre',
        titleEn: 'Respond',
        textFr: 'Indices contextuels, aucun compte à rebours, même notion dans un nouveau contexte et difficulté qui monte par petites marches.',
        textEn: 'Contextual cues, no countdown, the same idea in a new context and difficulty rising in small steps.',
      },
      {
        id: 'verify',
        titleFr: 'S’approprier',
        titleEn: 'Make it yours',
        textFr: 'Retrouver plus tard, initier une réponse et réussir sans échafaudage.',
        textEn: 'Recall later, initiate an answer and succeed without scaffolding.',
      },
    ],
    responseTitleFr: 'Sécuriser le rappel',
    responseTitleEn: 'Protect recall',
    responseFr: 'Aucun chrono : un indice de sens réactive le mot sans donner la réponse.',
    responseEn: 'No countdown: a meaning cue reactivates the word without giving it away.',
    microRuleFr: 'Indice, jamais la réponse',
    microRuleEn: 'A cue, never the answer',
  },
  {
    id: 'traveler',
    number: '04',
    Icon: FiCompass,
    accent: '93, 222, 189',
    image: travelerImage,
    thumbnail: travelerThumbnail,
    imagePosition: 'center 45%',
    titleFr: 'Voyageurs',
    titleEn: 'Travelers',
    priorityFr: 'Autonomie sur place',
    priorityEn: 'On-the-ground agency',
    situationFr: 'Départ dans cinq jours. Pas besoin d’un programme complet : l’objectif est de rester autonome quand la situation change.',
    situationEn: 'Departure in five days. They do not need a full curriculum—they want to stay resourceful when the situation changes.',
    metrics: [
      ['Départ', 'Departure', 'J−5', 'D−5'],
      ['Session', 'Session', '8 min', '8 min'],
      ['Contrainte', 'Constraint', 'Bruit + imprévu', 'Noise + change'],
    ],
    calibrations: [
      ['Cadence', 'Pace', 'Rapide', 'Rapid', 92],
      ['Niveau d’aide', 'Support level', 'Contextuel', 'Contextual', 58],
      ['Pression réaliste', 'Real-world pressure', 'Variable', 'Variable', 72],
      ['Preuve de progrès', 'Evidence of progress', 'Échange mené', 'Exchange completed', 84],
    ],
    fingerprintFr: '8 min · rythme rapide · aide contextuelle · priorité : autonomie',
    fingerprintEn: '8 min · rapid pace · contextual support · priority: autonomy',
    rationaleFr: 'L’échéance est courte : le parcours privilégie la résilience dans quelques échanges essentiels plutôt que la couverture d’un niveau entier.',
    rationaleEn: 'The deadline is short: the journey favors resilience in a few essential exchanges over covering an entire level.',
    phases: [
      {
        id: 'observe',
        titleFr: 'Prioriser',
        titleEn: 'Prioritize',
        textFr: 'Échéance, intentions essentielles, compréhension dans le bruit et stratégies de réparation définissent l’urgence.',
        textEn: 'Trip horizon, essential intents, listening in noise and repair strategies define the urgency.',
      },
      {
        id: 'decide',
        titleFr: 'Simuler l’imprévu',
        titleEn: 'Rehearse the unexpected',
        textFr: 'Prioriser l’urgence et l’imprévisible plutôt que couvrir tout le niveau.',
        textEn: 'Prioritize urgency and unpredictability instead of covering an entire level.',
      },
      {
        id: 'adapt',
        titleFr: 'Stabiliser les réflexes',
        titleEn: 'Lock in the reflexes',
        textFr: 'Débit et bruit variables, branches imprévues, formulations de secours et répétitions compactes.',
        textEn: 'Variable speech and noise, unexpected branches, repair language and compact rehearsal.',
      },
      {
        id: 'verify',
        titleFr: 'Transférer sur place',
        titleEn: 'Carry it into the real moment',
        textFr: 'Mener l’échange à terme même si l’accent, les mots ou le contexte changent.',
        textEn: 'Complete the exchange even when accent, wording or context changes.',
      },
    ],
    responseTitleFr: 'Maintenir l’échange',
    responseTitleEn: 'Keep the exchange moving',
    responseFr: 'Rendre disponible une stratégie de réparation : « Pouvez-vous répéter plus lentement ? »',
    responseEn: 'Surface a repair strategy: “Could you repeat more slowly?”',
    microRuleFr: 'L’utilité avant la perfection',
    microRuleEn: 'Utility before perfection',
  },
]

const useReveal = (threshold = 0.12) => {
  const ref = useRef(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isInView, setIsInView] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined

    if (typeof window.IntersectionObserver !== 'function') {
      setIsVisible(true)
      setIsInView(true)
      return undefined
    }

    const observer = new window.IntersectionObserver(([entry]) => {
      setIsInView(entry.isIntersecting)
      if (entry.isIntersecting) setIsVisible(true)
    }, { threshold })

    observer.observe(node)
    return () => observer.disconnect()
  }, [threshold])

  return [ref, isVisible, isInView]
}

const HeroNetwork = () => (
  <div className="use-case-page__hero-network" aria-hidden="true">
    <svg viewBox="0 0 1180 430" preserveAspectRatio="xMidYMid slice" focusable="false">
      <g className="use-case-page__hero-routes">
        <path d="M650 356 C710 320 742 264 846 210 C900 182 946 166 994 128" pathLength="1" />
        <path d="M760 366 C786 310 824 270 890 218 C930 187 964 164 994 128" pathLength="1" />
        <path d="M890 374 C902 306 922 255 958 198 C974 172 986 150 994 128" pathLength="1" />
        <path d="M1026 366 C1030 300 1026 245 1014 192 C1009 168 1002 146 994 128" pathLength="1" />
      </g>
      <g className="use-case-page__hero-points">
        <circle cx="650" cy="356" r="5" />
        <circle cx="760" cy="366" r="5" />
        <circle cx="890" cy="374" r="5" />
        <circle cx="1026" cy="366" r="5" />
        <circle className="use-case-page__hero-core-point" cx="994" cy="128" r="9" />
      </g>
    </svg>
    <span className="use-case-page__hero-packet use-case-page__hero-packet--one" />
    <span className="use-case-page__hero-packet use-case-page__hero-packet--two" />
    <span className="use-case-page__hero-packet use-case-page__hero-packet--three" />
  </div>
)

const EngineMap = ({ profile }) => {
  const ProfileIcon = profile.Icon

  return (
    <div className="use-case-engine__map" aria-hidden="true">
      <svg viewBox="0 0 360 250" preserveAspectRatio="none" focusable="false">
        <path className="use-case-engine__route use-case-engine__route--input" d="M18 125 C74 125 91 125 136 125" pathLength="1" />
        <path className="use-case-engine__route use-case-engine__route--output" d="M196 125 C232 125 247 45 331 45" pathLength="1" />
        <path className="use-case-engine__route use-case-engine__route--output" d="M196 125 C240 125 260 98 331 98" pathLength="1" />
        <path className="use-case-engine__route use-case-engine__route--output" d="M196 125 C240 125 260 152 331 152" pathLength="1" />
        <path className="use-case-engine__route use-case-engine__route--output" d="M196 125 C232 125 247 205 331 205" pathLength="1" />
      </svg>

      <span className="use-case-engine__input-node">
        <FiZap />
      </span>

      <span className="use-case-engine__core">
        <span className="use-case-engine__core-orbit use-case-engine__core-orbit--outer" />
        <span className="use-case-engine__core-orbit use-case-engine__core-orbit--inner" />
        <span className="use-case-engine__core-glow" />
        <ProfileIcon />
      </span>

      <span className="use-case-engine__output use-case-engine__output--one"><FiLayers /></span>
      <span className="use-case-engine__output use-case-engine__output--two"><FiSliders /></span>
      <span className="use-case-engine__output use-case-engine__output--three"><FiMessageCircle /></span>
      <span className="use-case-engine__output use-case-engine__output--four"><FiClock /></span>
      <span className="use-case-engine__scan" />
    </div>
  )
}

const ProfileSelector = ({
  activeProfile,
  onActivate,
  onKeyDown,
  buttonRefs,
}) => (
  <div
    className="use-case-explorer__profile-tabs"
    role="tablist"
    aria-orientation="horizontal"
    aria-label="Profils adaptatifs / Adaptive profiles"
  >
    {profiles.map((profile, index) => {
      const ProfileIcon = profile.Icon
      const isActive = profile.id === activeProfile.id

      return (
        <button
          className={`use-case-profile-tab${isActive ? ' use-case-profile-tab--active' : ''}`}
          id={`profile-tab-${profile.id}`}
          key={profile.id}
          type="button"
          role="tab"
          aria-selected={isActive}
          aria-controls="adaptive-profile-panel"
          tabIndex={isActive ? 0 : -1}
          ref={(node) => { buttonRefs.current[index] = node }}
          style={{ '--profile-rgb': profile.accent }}
          onClick={() => onActivate(profile.id)}
          onKeyDown={(event) => onKeyDown(event, index)}
        >
          <span className="use-case-profile-tab__index" aria-hidden="true">{profile.number}</span>
          <span className="use-case-profile-tab__portrait" aria-hidden="true">
            <img
              src={profile.thumbnail}
              alt=""
              loading="lazy"
              decoding="async"
              width="240"
              height="300"
              style={{ objectPosition: profile.imagePosition }}
            />
            <span className="use-case-profile-tab__icon"><ProfileIcon /></span>
          </span>
          <span className="use-case-profile-tab__copy">
            <strong lang="fr">{profile.titleFr}</strong>
            <small lang="en">{profile.titleEn}</small>
            <span className="use-case-profile-tab__priority">
              <span lang="fr">{profile.priorityFr}</span>
              <small lang="en">{profile.priorityEn}</small>
            </span>
          </span>
          <FiArrowRight className="use-case-profile-tab__arrow" aria-hidden="true" />
        </button>
      )
    })}
  </div>
)

const ProfileStage = ({ profile, runId, onReplay }) => {
  const ProfileIcon = profile.Icon

  return (
    <article
      className="use-case-explorer__stage"
      id="adaptive-profile-panel"
      role="tabpanel"
      aria-labelledby={`profile-tab-${profile.id}`}
      style={{ '--profile-rgb': profile.accent }}
    >
      <div
        className="use-case-explorer__stage-scan"
        key={`stage-scan-${profile.id}-${runId}`}
        aria-hidden="true"
      />

      <header className="use-case-explorer__stage-header">
        <div className="use-case-explorer__stage-identity">
          <span className="use-case-explorer__stage-icon" aria-hidden="true"><ProfileIcon /></span>
          <div>
            <p className="use-case-explorer__stage-kicker">
              <span lang="fr">Parcours actif {profile.number}</span>
              <small lang="en">/ Active journey</small>
            </p>
            <h3>
              <span lang="fr">{profile.titleFr}</span>
              <small lang="en">{profile.titleEn}</small>
            </h3>
          </div>
        </div>

        <div className="use-case-explorer__stage-status">
          <span><i aria-hidden="true" />Synapse active</span>
          <button type="button" onClick={onReplay}>
            <FiRotateCw aria-hidden="true" />
            <span>
              <span lang="fr">Relancer le signal</span>
              <small lang="en">Replay adaptation</small>
            </span>
          </button>
        </div>
      </header>

      <div className="use-case-explorer__stage-content" key={`${profile.id}-${runId}`}>
        <section className="use-case-context" aria-labelledby={`context-title-${profile.id}`}>
          <div className="use-case-module-heading">
            <span className="use-case-module-heading__icon" aria-hidden="true"><FiActivity /></span>
            <div>
              <h4 id={`context-title-${profile.id}`}>
                <span lang="fr">Situation de départ</span>
                <small lang="en">Starting situation</small>
              </h4>
              <span className="use-case-module-heading__status">
                <i aria-hidden="true" />
                <span lang="fr">Exemple</span>
                <small lang="en">Example</small>
              </span>
            </div>
          </div>

          <figure
            className="use-case-context__visual"
            style={{ '--profile-image-position': profile.imagePosition }}
            data-profile-image={profile.id}
          >
            <img
              src={profile.image}
              alt=""
              loading="lazy"
              decoding="async"
              width="960"
              height="720"
            />
            <span className="use-case-context__visual-glow" aria-hidden="true" />
            <figcaption>
              <span>
                <span lang="fr">Le profil en situation</span>
                <small lang="en">Profile in context</small>
              </span>
              <strong>
                <span lang="fr">{profile.priorityFr}</span>
                <small lang="en">{profile.priorityEn}</small>
              </strong>
            </figcaption>
          </figure>

          <div className="use-case-context__situation">
            <span className="use-case-context__quote" aria-hidden="true">“</span>
            <p lang="fr">{profile.situationFr}</p>
            <p lang="en">{profile.situationEn}</p>
          </div>

          <dl className="use-case-context__metrics">
            {profile.metrics.map(([labelFr, labelEn, valueFr, valueEn]) => (
              <div key={labelFr}>
                <dt>
                  <span lang="fr">{labelFr}</span>
                  <small lang="en">{labelEn}</small>
                </dt>
                <dd>
                  <span lang="fr">{valueFr}</span>
                  {valueEn !== valueFr && <small lang="en">{valueEn}</small>}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="use-case-engine" aria-labelledby={`engine-title-${profile.id}`}>
          <div className="use-case-module-heading">
            <span className="use-case-module-heading__icon" aria-hidden="true"><FiCpu /></span>
            <div>
              <h4 id={`engine-title-${profile.id}`}>
                <span lang="fr">Ce qui change vraiment</span>
                <small lang="en">What actually changes</small>
              </h4>
              <span className="use-case-module-heading__status">
                <i aria-hidden="true" />
                <span lang="fr">Recalcul en direct</span>
                <small lang="en">Live recalibration</small>
              </span>
            </div>
          </div>

          <EngineMap profile={profile} />

          <div className="use-case-engine__calibrations">
            {profile.calibrations.map(([labelFr, labelEn, valueFr, valueEn, value], index) => (
              <div
                className="use-case-engine__calibration"
                key={labelFr}
                style={{ '--bar-value': `${value}%`, '--bar-index': index }}
              >
                <div>
                  <p>
                    <span lang="fr">{labelFr}</span>
                    <small lang="en">{labelEn}</small>
                  </p>
                  <strong>
                    <span lang="fr">{valueFr}</span>
                    <small lang="en">{valueEn}</small>
                  </strong>
                </div>
                <span className="use-case-engine__bar" aria-hidden="true"><i /></span>
              </div>
            ))}
          </div>
        </section>

        <section className="use-case-strategy" aria-labelledby={`strategy-title-${profile.id}`}>
          <div className="use-case-module-heading">
            <span className="use-case-module-heading__icon" aria-hidden="true"><FiTarget /></span>
            <div>
              <h4 id={`strategy-title-${profile.id}`}>
                <span lang="fr">La trajectoire calculée</span>
                <small lang="en">The computed trajectory</small>
              </h4>
              <span className="use-case-module-heading__status">
                <i aria-hidden="true" />
                <span lang="fr">4 décisions coordonnées</span>
                <small lang="en">4 coordinated decisions</small>
              </span>
            </div>
          </div>

          <ol className="use-case-strategy__timeline">
            {profile.phases.map((phase, index) => {
              const meta = phaseMeta[phase.id]
              const PhaseIcon = meta.Icon

              return (
                <li key={phase.id} style={{ '--step-index': index }}>
                  <span className="use-case-strategy__node" aria-hidden="true">
                    <span>{meta.number}</span>
                    <PhaseIcon />
                  </span>
                  <div className="use-case-strategy__step-copy">
                    <p className="use-case-strategy__phase">
                      <span lang="fr">{meta.labelFr}</span>
                      <small lang="en">{meta.labelEn}</small>
                    </p>
                    <h5>
                      <span lang="fr">{phase.titleFr}</span>
                      <small lang="en">{phase.titleEn}</small>
                    </h5>
                    <p lang="fr">{phase.textFr}</p>
                    <p lang="en">{phase.textEn}</p>
                    <span className="use-case-strategy__note">
                      <span lang="fr">{meta.noteFr}</span>
                      <small lang="en">{meta.noteEn}</small>
                    </span>
                  </div>
                </li>
              )
            })}
          </ol>
        </section>
      </div>

      <div
        className="use-case-explorer__decision"
        key={`decision-${profile.id}-${runId}`}
      >
        <span className="use-case-explorer__decision-pulse" aria-hidden="true">
          <FiZap />
        </span>
        <div className="use-case-explorer__decision-copy">
          <p>
            <span lang="fr">Pourquoi cette trajectoire ?</span>
            <small lang="en">Why this route?</small>
          </p>
          <span lang="fr">{profile.rationaleFr}</span>
          <span lang="en">{profile.rationaleEn}</span>
        </div>
        <div className="use-case-explorer__fingerprint">
          <span><FiCheck aria-hidden="true" />Adaptive fingerprint</span>
          <strong lang="fr">{profile.fingerprintFr}</strong>
          <small lang="en">{profile.fingerprintEn}</small>
        </div>
      </div>
    </article>
  )
}

const SameSignal = ({ sectionRef, isVisible, isInView }) => (
  <section
    className={`use-case-signal${isVisible ? ' use-case-signal--visible' : ''}${isInView ? ' use-case-signal--in-view' : ''}`}
    ref={sectionRef}
    aria-labelledby="same-signal-title"
  >
    <header className="use-case-signal__header">
      <p className="use-case-page__eyebrow">
        <i aria-hidden="true" />
        <span lang="fr">La preuve par la décision</span>
        <small lang="en">Adaptation, made visible</small>
      </p>
      <div className="use-case-page__section-title">
        <span aria-hidden="true" />
        <h2 id="same-signal-title">
          <span lang="fr">Un même signal. <strong>Quatre décisions.</strong></span>
          <small lang="en">One signal. <strong>Four decisions.</strong></small>
        </h2>
        <span aria-hidden="true" />
      </div>
      <div className="use-case-signal__intro">
        <p lang="fr">Une hésitation ne veut pas dire la même chose pour tout le monde. Le profil, l’objectif et la trajectoire récente changent la réponse.</p>
        <p lang="en">A hesitation does not mean the same thing for everyone. Profile, purpose and recent trajectory change the response.</p>
      </div>
    </header>

    <div className="use-case-signal__source">
      <span className="use-case-signal__source-wave" aria-hidden="true" />
      <span className="use-case-signal__source-icon" aria-hidden="true"><FiVolume2 /></span>
      <div>
        <span lang="fr">Signal détecté</span>
        <small lang="en">Signal detected</small>
        <strong>00:04</strong>
      </div>
      <p>
        <span lang="fr">4 secondes de silence</span>
        <small lang="en">4 seconds of silence</small>
      </p>
    </div>

    <div className="use-case-signal__branch" aria-hidden="true">
      <svg viewBox="0 0 1000 116" preserveAspectRatio="none" focusable="false">
        <path d="M500 0 L500 24 C500 58 125 48 125 112" pathLength="1" />
        <path d="M500 0 L500 24 C500 58 375 48 375 112" pathLength="1" />
        <path d="M500 0 L500 24 C500 58 625 48 625 112" pathLength="1" />
        <path d="M500 0 L500 24 C500 58 875 48 875 112" pathLength="1" />
        <circle cx="500" cy="2" r="5" />
        <circle cx="125" cy="112" r="4" />
        <circle cx="375" cy="112" r="4" />
        <circle cx="625" cy="112" r="4" />
        <circle cx="875" cy="112" r="4" />
      </svg>
    </div>

    <div className="use-case-signal__responses">
      {profiles.map((profile, index) => {
        const ProfileIcon = profile.Icon

        return (
          <article
            className="use-case-signal__response"
            key={profile.id}
            style={{ '--profile-rgb': profile.accent, '--response-index': index }}
          >
            <header>
              <span className="use-case-signal__response-icon" aria-hidden="true"><ProfileIcon /></span>
              <span className="use-case-signal__response-number" aria-hidden="true">{profile.number}</span>
              <div>
                <h3>
                  <span lang="fr">{profile.titleFr}</span>
                  <small lang="en">{profile.titleEn}</small>
                </h3>
                <span className="use-case-signal__response-status">
                  <i aria-hidden="true" />
                  <span lang="fr">Route recalculée</span>
                  <small lang="en">Route changed</small>
                </span>
              </div>
            </header>
            <div className="use-case-signal__response-copy">
              <strong>
                <span lang="fr">{profile.responseTitleFr}</span>
                <small lang="en">{profile.responseTitleEn}</small>
              </strong>
              <p lang="fr">{profile.responseFr}</p>
              <p lang="en">{profile.responseEn}</p>
            </div>
            <div className="use-case-signal__response-rule">
              <FiShield aria-hidden="true" />
              <span>
                <span lang="fr">{profile.microRuleFr}</span>
                <small lang="en">{profile.microRuleEn}</small>
              </span>
            </div>
          </article>
        )
      })}
    </div>
  </section>
)

const UseCase = () => {
  const [activeProfileId, setActiveProfileId] = useState(profiles[0].id)
  const [runId, setRunId] = useState(0)
  const [profilesRef, profilesVisible, profilesInView] = useReveal(0.08)
  const [signalRef, signalVisible, signalInView] = useReveal(0.1)
  const buttonRefs = useRef([])
  const activeProfile = profiles.find(({ id }) => id === activeProfileId) || profiles[0]

  const activateProfile = (profileId) => {
    setActiveProfileId(profileId)
    setRunId((current) => current + 1)
  }

  const handleProfileKeyDown = (event, currentIndex) => {
    const lastIndex = profiles.length - 1
    let nextIndex = null

    if (event.key === 'ArrowRight') {
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1
    } else if (event.key === 'ArrowLeft') {
      nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = lastIndex
    }

    if (nextIndex === null) return
    event.preventDefault()
    activateProfile(profiles[nextIndex].id)
    buttonRefs.current[nextIndex]?.focus()
  }

  const handlePointerMove = (event) => {
    if (
      typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return

    const bounds = event.currentTarget.getBoundingClientRect()
    const x = ((event.clientX - bounds.left) / bounds.width) * 100
    const y = ((event.clientY - bounds.top) / bounds.height) * 100
    event.currentTarget.style.setProperty('--pointer-x', `${x}%`)
    event.currentTarget.style.setProperty('--pointer-y', `${y}%`)
  }

  const resetPointerGlow = (event) => {
    if (
      typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) return

    event.currentTarget.style.setProperty('--pointer-x', '50%')
    event.currentTarget.style.setProperty('--pointer-y', '18%')
  }

  return (
    <main className="use-case-page" id="use-case" tabIndex="-1">
      <section className="use-case-page__hero" aria-labelledby="use-case-title">
        <div className="use-case-page__hero-art" aria-hidden="true">
          <img
            className="use-case-page__hero-image"
            src={useCaseHero}
            alt=""
            width="1774"
            height="887"
            decoding="async"
            fetchPriority="high"
          />
          <div className="use-case-page__hero-shade" />
          <HeroNetwork />
        </div>
        <div className="use-case-page__hero-copy">
          <p className="use-case-page__hero-eyebrow">
            <span lang="fr">Intelligence adaptative</span>
            <i aria-hidden="true" />
            <small lang="en">Adaptive intelligence</small>
          </p>
          <h1 id="use-case-title">
            <span className="use-case-page__title-fr" lang="fr">Cas <strong>d’usage</strong></span>
            <small className="use-case-page__title-en" lang="en">Use cases</small>
          </h1>
          <div className="use-case-page__hero-intro">
            <p lang="fr">Chaque parcours est unique. OctoSynapse ajuste la stratégie à votre objectif, votre rythme et ce qui se passe réellement.</p>
            <p lang="en">Every journey is unique. OctoSynapse adjusts the strategy to your goal, your pace and what actually happens.</p>
          </div>
        </div>
        <div className="use-case-page__hero-status" aria-hidden="true">
          <span className="use-case-page__hero-status-core"><FiCpu /></span>
          <span>
            <strong>04</strong>
            <small>adaptive routes</small>
          </span>
          <i />
          <span>
            <strong>01</strong>
            <small>living engine</small>
          </span>
        </div>
      </section>

      <section
        className={`use-case-page__profiles${profilesVisible ? ' use-case-page__profiles--visible' : ''}${profilesInView ? ' use-case-page__profiles--in-view' : ''}`}
        ref={profilesRef}
        aria-labelledby="profiles-title"
      >
        <header className="use-case-page__profiles-header">
          <p className="use-case-page__eyebrow">
            <i aria-hidden="true" />
            <span lang="fr">Le moteur adaptatif, en situation</span>
            <small lang="en">The adaptive engine at work</small>
          </p>
          <div className="use-case-page__section-title">
            <span aria-hidden="true" />
            <h2 id="profiles-title">
              <span lang="fr">Quatre profils, <strong>quatre parcours.</strong></span>
              <small lang="en">Four profiles, <strong>four journeys.</strong></small>
            </h2>
            <span aria-hidden="true" />
          </div>
          <div className="use-case-page__profiles-lead">
            <p lang="fr">Pas quatre versions du même cours. Quatre trajectoires qui changent réellement le rythme, l’aide, le défi et le suivi.</p>
            <p lang="en">Not four versions of the same lesson. Four journeys that truly change pace, support, challenge and follow-through.</p>
          </div>
        </header>

        <div
          className="use-case-explorer"
          onPointerMove={handlePointerMove}
          onPointerLeave={resetPointerGlow}
        >
          <span className="use-case-explorer__ambient use-case-explorer__ambient--one" aria-hidden="true" />
          <span className="use-case-explorer__ambient use-case-explorer__ambient--two" aria-hidden="true" />
          <ProfileSelector
            activeProfile={activeProfile}
            onActivate={activateProfile}
            onKeyDown={handleProfileKeyDown}
            buttonRefs={buttonRefs}
          />
          <ProfileStage
            profile={activeProfile}
            runId={runId}
            onReplay={() => setRunId((current) => current + 1)}
          />
          <p className="use-case-explorer__trust">
            <FiCheckCircle aria-hidden="true" />
            <span>
              <span lang="fr">Ces profils sont des points de départ, pas des cases. Votre parcours peut combiner plusieurs besoins et évoluer.</span>
              <small lang="en">These profiles are starting points, not boxes. Your journey can combine needs and evolve.</small>
            </span>
          </p>
          <p className="use-case-page__sr-only" aria-live="polite" aria-atomic="true">
            {activeProfile.titleFr}. {activeProfile.titleEn}. {activeProfile.fingerprintFr}. {activeProfile.fingerprintEn}.
          </p>
        </div>
      </section>

      <SameSignal
        sectionRef={signalRef}
        isVisible={signalVisible}
        isInView={signalInView}
      />

      <FinalCtaPanel
        id="use-case-cta"
        headingId="use-case-cta-title"
        titleFr="Votre profil n’est pas une étiquette. C’est un point de départ qui évolue."
        titleEn="Your profile is not a label. It is a starting point that keeps evolving."
        bodyFr="Entrez dans une scène et laissez le parcours s’ajuster à ce que vous faites réellement."
        bodyEn="Enter a scene and let the journey adapt to what you actually do."
        href={APP_ENTRY_HREF}
        label="Tester une scène"
        labelEn="Enter a scene"
        activatingLabel="Ouverture de la scène…"
        activatingLabelEn="Opening scene…"
      />
    </main>
  )
}

export default UseCase
